import {
  Server, Cpu, HardDrive, Cloud, Database, Globe, Rocket,
  Bot, Zap, Flame, Shield, Crown, Star, Heart, Diamond,
  TrendingUp, Activity, Briefcase, Wrench, Layers,
  Sparkles, Target, Anchor, Compass, Award, Box,
  type LucideIcon,
} from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover'

/**
 * Catalogue of icons the user can pick for their server.
 * Stored as the string key in `panel_settings.server_icon`, then
 * resolved to a Lucide component on render. Keep this list stable —
 * removing a key here would silently break a server's saved choice.
 *
 * If you want to add more, just append. Order = display order.
 */
export const SERVER_ICONS: Record<string, LucideIcon> = {
  server: Server,
  cpu: Cpu,
  drive: HardDrive,
  cloud: Cloud,
  db: Database,
  globe: Globe,
  rocket: Rocket,
  bot: Bot,
  zap: Zap,
  flame: Flame,
  shield: Shield,
  crown: Crown,
  star: Star,
  heart: Heart,
  diamond: Diamond,
  trending: TrendingUp,
  activity: Activity,
  briefcase: Briefcase,
  wrench: Wrench,
  layers: Layers,
  sparkles: Sparkles,
  target: Target,
  anchor: Anchor,
  compass: Compass,
  award: Award,
  box: Box,
}

/** Resolve an icon key (string from panel settings) to a component.
 *  Returns the Server icon if the key is unknown / empty. */
export function resolveServerIcon(key: string | undefined): LucideIcon {
  if (!key) return Server
  return SERVER_ICONS[key] || Server
}

interface ServerIconPickerProps {
  value: string
  onChange: (next: string) => void
}

/**
 * Settings-page row with a popover icon picker. Renders the
 * current selection as a tile button + colour-styled dropdown grid.
 */
export function ServerIconPicker({ value, onChange }: ServerIconPickerProps) {
  const Selected = resolveServerIcon(value)
  const items = Object.entries(SERVER_ICONS) as [string, LucideIcon][]
  return (
    <div className="space-y-1.5 min-w-0">
      <Label>Иконка сервера</Label>
      <div className="flex items-center gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-12 w-12 p-0 rounded-lg shrink-0"
              title={value ? `Иконка: ${value}` : 'Выбрать иконку'}
            >
              <Selected className="w-5 h-5 text-primary" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[316px] p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-2">
              Выберите иконку
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {items.map(([key, Icon]) => {
                const active = (value || 'server') === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    title={key}
                    className={`h-9 w-9 rounded-md flex items-center justify-center border transition-colors ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                )
              })}
            </div>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="mt-3 text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              >
                Сбросить (по умолчанию)
              </button>
            )}
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground">
          Видна в переключателе серверов слева в сайдбаре.
        </p>
      </div>
    </div>
  )
}
